import { Box } from '@mui/material';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface FloatingActionStackContextValue {
  container: HTMLElement | null;
  /**
   * Registers a slot as mounted and returns a cleanup that deregisters it.
   * `null` means no provider is present (e.g. isolated tests), so slots fall
   * back to rendering inline.
   */
  register: (() => () => void) | null;
}

const FloatingActionStackContext = createContext<FloatingActionStackContextValue>({
  container: null,
  register: null,
});

/** Distance of the stack from the bottom-right viewport corner, in px. */
const STACK_EDGE_OFFSET = 24;

/**
 * Provides a single shared bottom-right floating stack for the app.
 *
 * Renders a fixed, transparent, fit-content flex row that lays out every
 * registered floating action (FAB) with consistent spacing, so corner buttons
 * never overlap regardless of where in the tree they are mounted. The container
 * takes no space and is invisible/click-through when empty — it only "appears"
 * once something is portalled into it.
 *
 * Mount this once high enough to be an ancestor of every {@link FloatingActionSlot}.
 */
export function FloatingActionStackProvider({ children }: { children: React.ReactNode }) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [slotCount, setSlotCount] = useState(0);

  const register = useCallback(() => {
    setSlotCount((count) => count + 1);
    return () => setSlotCount((count) => count - 1);
  }, []);

  return (
    <FloatingActionStackContext.Provider value={{ container, register }}>
      {children}
      {/* Only mount the container once something is registered, so it leaves no
          trace in the DOM when there are no actions to show. */}
      {slotCount > 0 && (
        <Box
          ref={setContainer}
          className="floating-action-stack"
          sx={{
            position: 'fixed',
            bottom: STACK_EDGE_OFFSET,
            right: STACK_EDGE_OFFSET,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: 1.5,
            width: 'fit-content',
            // Transparent and click-through; each registered action re-enables
            // pointer events for its own footprint so the gaps stay non-blocking.
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' },
          }}
        />
      )}
    </FloatingActionStackContext.Provider>
  );
}

/**
 * Registers its children into the shared {@link FloatingActionStackProvider}.
 *
 * Use `order` to control left-to-right placement within the row (lower = further
 * left, so the highest order sits in the bottom-right corner). When no provider
 * is present (e.g. in isolated tests) the children render inline so they remain
 * discoverable.
 */
export function FloatingActionSlot({
  order = 0,
  children,
}: {
  order?: number;
  children: React.ReactNode;
}) {
  const { container, register } = useContext(FloatingActionStackContext);

  // Registering drives the provider to mount its container; deregistering on
  // unmount lets the provider drop the container when nothing is left.
  useEffect(() => {
    if (!register) return undefined;
    return register();
  }, [register]);

  // No provider in the tree (e.g. isolated tests) — render inline so the action
  // stays discoverable.
  if (!register) {
    return <>{children}</>;
  }

  // Provider present but its container has not mounted yet (first render after
  // registering) — render nothing for this frame rather than flashing inline.
  if (!container) {
    return null;
  }

  return createPortal(<Box sx={{ display: 'flex', order }}>{children}</Box>, container);
}
